import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  RefreshCw,
  Download,
  ExternalLink,
  Copy,
  Braces,
  Settings2,
  Terminal,
  Trash2,
  Search,
  X,
  ListOrdered,
  CalendarDays,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useDebounce } from '@/hooks/useDebounce';
import { useHasAnyRole } from '@/hooks/useSession';
import { formatDateTime } from '@/lib/utils';
import {
  transmitterRawEventsKeys,
  useTransmitterRawEventDetail,
  useTransmitterRawEvents,
} from '../hooks/useTransmitterRawEvents';
import type {
  TransmitterRawEventDetail,
  TransmitterRawEventSummary,
} from '../types';

const VISIBILITY_STORAGE_KEY = 'atek-transmitter-raw-events-column-visibility';
const SOURCE_API_CHECKER_HTTP = 'api_checker_http';
const SOURCE_LORA_STACK_INSERTDATA = 'lora_stack_insertdata';

const SOURCE_OPTIONS = [
  { label: 'All sources', value: 'all' },
  { label: 'UDP Server', value: 'udp_server' },
  { label: 'api_checker HTTP', value: SOURCE_API_CHECKER_HTTP },
  { label: 'LoRaWAN via insertData', value: SOURCE_LORA_STACK_INSERTDATA },
  { label: 'LoRaWAN Pub/Sub', value: 'lorawan_packets' },
];

const STATUS_OPTIONS = [
  { label: 'All statuses', value: 'all' },
  { label: 'Accepted', value: 'accepted' },
  { label: 'Rejected', value: 'rejected' },
  { label: 'Received', value: 'received' },
  { label: 'Publish failed', value: 'publish_failed' },
];

const BACKFILL_SOURCE_OPTIONS = SOURCE_OPTIONS.filter(
  (option) => option.value !== 'all' && option.value !== SOURCE_LORA_STACK_INSERTDATA,
);
const BACKFILL_STATUS_OPTIONS = STATUS_OPTIONS.filter((option) => option.value !== 'all');
const BACKFILL_URL_OPTIONS = [
  { label: 'Auto', value: 'auto' },
  { label: 'insertData', value: '/v1/insertData' },
  { label: 'XBee insert', value: '/v1/xbee/insert' },
];

type TimePreset = 'custom' | 'last_5m' | 'last_30m' | 'last_2h';
type LastPacketLimit = 20 | 100 | 500;
type QueryMode = 'latest' | 'dateRange';
type BackfillScope = 'stream' | 'organisation';
type BackfillMode = 'dry-run' | 'execute';

const LAST_PACKET_OPTIONS: Array<{ label: string; value: LastPacketLimit }> = [
  { label: 'Last 20 packets', value: 20 },
  { label: 'Last 100 packets', value: 100 },
  { label: 'Last 500 packets', value: 500 },
];

const DEFAULT_LAST_PACKET_LIMIT: LastPacketLimit = 20;

const TIME_PRESET_OPTIONS: Array<{ label: string; value: TimePreset }> = [
  { label: 'Custom', value: 'custom' },
  { label: 'Last 5 min', value: 'last_5m' },
  { label: 'Last 30 min', value: 'last_30m' },
  { label: 'Last 2 hours', value: 'last_2h' },
];

const DEFAULT_QUERY_MODE: QueryMode = 'latest';

type DateTimeField = 'From' | 'To';

type ColumnKey =
  | 'received_at'
  | 'source'
  | 'status'
  | 'device'
  | 'identifier'
  | 'remote'
  | 'payload'
  | 'interpret';

const COLUMN_LABELS: Record<ColumnKey, string> = {
  received_at: 'Received',
  source: 'Source',
  status: 'Status',
  device: 'Device',
  identifier: 'Packet',
  remote: 'Remote',
  payload: 'Payload Preview',
  interpret: 'Interpreter',
};

const DEFAULT_COLUMN_VISIBILITY: Record<ColumnKey, boolean> = {
  received_at: true,
  source: true,
  status: true,
  device: true,
  identifier: true,
  interpret: true,
  remote: false,
  payload: true,
};

function loadColumnVisibility(): Record<ColumnKey, boolean> {
  try {
    const stored = localStorage.getItem(VISIBILITY_STORAGE_KEY);
    if (!stored) return DEFAULT_COLUMN_VISIBILITY;
    return { ...DEFAULT_COLUMN_VISIBILITY, ...JSON.parse(stored) };
  } catch {
    return DEFAULT_COLUMN_VISIBILITY;
  }
}

function toLocalDateTimeValue(date: Date): string {
  const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return offsetDate.toISOString().slice(0, 16);
}

function parseLocalDateTimeValue(value: string): Date | null {
  const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/);
  if (!match) return null;

  const [, yearValue, monthValue, dayValue, hourValue, minuteValue, secondValue = '0', millisecondValue = '0'] = match;
  const year = Number(yearValue);
  const month = Number(monthValue);
  const day = Number(dayValue);
  const hour = Number(hourValue);
  const minute = Number(minuteValue);
  const second = Number(secondValue);
  const millisecond = Number(millisecondValue.padEnd(3, '0'));
  const date = new Date(year, month - 1, day, hour, minute, second, millisecond);

  if (
    date.getFullYear() !== year
    || date.getMonth() !== month - 1
    || date.getDate() !== day
    || date.getHours() !== hour
    || date.getMinutes() !== minute
    || date.getSeconds() !== second
    || date.getMilliseconds() !== millisecond
  ) {
    return null;
  }

  return date;
}

function isValidLocalDateTimeValue(value: string | null): value is string {
  return value !== null && parseLocalDateTimeValue(value) !== null;
}

function getInitialLocalDateTimeValue(value: string | null, fallback: string): string {
  return isValidLocalDateTimeValue(value) ? value : fallback;
}

function toIsoFromLocalDateTime(value: string): string | null {
  return parseLocalDateTimeValue(value)?.toISOString() ?? null;
}

function getDefaultFromValue(): string {
  return toLocalDateTimeValue(new Date(Date.now() - 24 * 60 * 60 * 1000));
}

function getDefaultToValue(): string {
  return toLocalDateTimeValue(new Date(Date.now()));
}

function isTimePreset(value: string | null): value is TimePreset {
  return value === 'custom' || value === 'last_5m' || value === 'last_30m' || value === 'last_2h';
}

function isQueryMode(value: string | null): value is QueryMode {
  return value === 'latest' || value === 'dateRange';
}

function getLastPacketLimit(value: string | null): LastPacketLimit {
  const parsed = Number(value);
  const option = LAST_PACKET_OPTIONS.find((packetOption) => packetOption.value === parsed);
  return option?.value || DEFAULT_LAST_PACKET_LIMIT;
}

function getInvalidDateTimeFields(fromIso: string | null, toIso: string | null): DateTimeField[] {
  const fields: DateTimeField[] = [];
  if (!fromIso) fields.push('From');
  if (!toIso) fields.push('To');
  return fields;
}

function resolveRelativeRange(preset: TimePreset, anchorMs: number): { from: string; to: string } | null {
  const to = new Date(anchorMs);
  const from = new Date(anchorMs);

  if (preset === 'last_5m') {
    from.setMinutes(from.getMinutes() - 5);
  } else if (preset === 'last_30m') {
    from.setMinutes(from.getMinutes() - 30);
  } else if (preset === 'last_2h') {
    from.setHours(from.getHours() - 2);
  } else {
    return null;
  }

  return {
    from: toLocalDateTimeValue(from),
    to: toLocalDateTimeValue(to),
  };
}

function formatNullableDate(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    return formatDateTime(value);
  } catch {
    return value;
  }
}

function getStatusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'rejected' || status === 'publish_failed') return 'destructive';
  if (status === 'accepted') return 'default';
  if (status === 'received') return 'secondary';
  return 'outline';
}

function getDeviceLabel(event: Pick<TransmitterRawEventSummary, 'dev_id' | 'dev_eui' | 'app_eui'>): string {
  return event.dev_id || event.dev_eui || event.app_eui || '—';
}

function downloadCsv(filename: string, rows: string[][]): void {
  const escapeCell = (value: string) => `"${value.replace(/"/g, '""')}"`;
  const csv = rows.map((row) => row.map(escapeCell).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function copyText(value: string, label: string): void {
  navigator.clipboard.writeText(value)
    .then(() => toast.success(`${label} copied`))
    .catch(() => toast.error(`Failed to copy ${label.toLowerCase()}`));
}

function tryPrettifyJson(value: string | null | undefined): string | null {
  if (!value) return null;

  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return null;
  }
}

function shellQuote(value: string): string {
  return `'${value.replace(/'/g, "'\\''")}'`;
}

function positiveIntegerOrNull(value: string): string | null {
  const trimmed = value.trim();
  return /^[1-9]\d*$/.test(trimmed) ? trimmed : null;
}

function organisationFlag(value: string): '--organisation-id' | '--org-number' {
  return /^[a-f0-9]{24}$/i.test(value.trim()) ? '--organisation-id' : '--org-number';
}

function getBackfillSourceForPageSource(pageSource: string): string {
  if (pageSource === SOURCE_LORA_STACK_INSERTDATA) return SOURCE_API_CHECKER_HTTP;
  return pageSource !== 'all' ? pageSource : SOURCE_API_CHECKER_HTTP;
}

function getBackfillUrlForPageSource(pageSource: string): string {
  return pageSource === SOURCE_LORA_STACK_INSERTDATA ? '/v1/insertData' : 'auto';
}

function getPacketInterpreterHref(payload: string | null | undefined): string | null {
  if (!payload) return null;
  return `/tools/packet-interpreter?packet=${encodeURIComponent(payload)}`;
}

export default function TransmitterRawEventsPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultFromValue = useMemo(() => getDefaultFromValue(), []);
  const defaultToValue = useMemo(() => getDefaultToValue(), []);
  const [ignoredInvalidDateTimeFields, setIgnoredInvalidDateTimeFields] = useState<DateTimeField[]>(() => {
    const fields: DateTimeField[] = [];
    const fromParam = searchParams.get('from');
    const toParam = searchParams.get('to');

    if (fromParam !== null && !isValidLocalDateTimeValue(fromParam)) fields.push('From');
    if (toParam !== null && !isValidLocalDateTimeValue(toParam)) fields.push('To');

    return fields;
  });
  const shouldResetInitialTimeRange = ignoredInvalidDateTimeFields.length > 0;
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [source, setSource] = useState(searchParams.get('source') || 'all');
  const [status, setStatus] = useState(searchParams.get('status') || 'all');
  const [queryMode, setQueryMode] = useState<QueryMode>(() => {
    const mode = searchParams.get('mode');
    return isQueryMode(mode) ? mode : DEFAULT_QUERY_MODE;
  });
  const [timePreset, setTimePreset] = useState<TimePreset>(() => {
    const preset = searchParams.get('preset');
    return isTimePreset(preset) ? preset : 'custom';
  });
  const [from, setFrom] = useState(() => (
    shouldResetInitialTimeRange
      ? defaultFromValue
      : getInitialLocalDateTimeValue(searchParams.get('from'), defaultFromValue)
  ));
  const [to, setTo] = useState(() => (
    shouldResetInitialTimeRange
      ? defaultToValue
      : getInitialLocalDateTimeValue(searchParams.get('to'), defaultToValue)
  ));
  const [pageNumber, setPageNumber] = useState(Number(searchParams.get('pageNumber') || '0'));
  const [pageLength, setPageLength] = useState<LastPacketLimit>(() => getLastPacketLimit(searchParams.get('pageLength')));
  const [selectedEventSummary, setSelectedEventSummary] = useState<TransmitterRawEventSummary | null>(null);
  const [showPrettyPayloadJson, setShowPrettyPayloadJson] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState<Record<ColumnKey, boolean>>(loadColumnVisibility);
  const [relativeAnchor, setRelativeAnchor] = useState(() => Date.now());
  const debouncedSearch = useDebounce(search, 300);
  const canUseRawEventsAdminTools = useHasAnyRole(['full_admin', 'director']);
  const [backfillDialogOpen, setBackfillDialogOpen] = useState(false);
  const [backfillScope, setBackfillScope] = useState<BackfillScope>('stream');
  const [backfillMode, setBackfillMode] = useState<BackfillMode>('dry-run');
  const [backfillStream, setBackfillStream] = useState(search.trim());
  const [backfillOrganisation, setBackfillOrganisation] = useState('');
  const [backfillSource, setBackfillSource] = useState(getBackfillSourceForPageSource(source));
  const [backfillStatus, setBackfillStatus] = useState(status !== 'all' ? status : 'received');
  const [backfillUrl, setBackfillUrl] = useState(getBackfillUrlForPageSource(source));
  const [backfillLimitPerStream, setBackfillLimitPerStream] = useState('10000');
  const [backfillMaxWritesPerStream, setBackfillMaxWritesPerStream] = useState('1000');
  const [backfillMaxTotalWrites, setBackfillMaxTotalWrites] = useState('1000');
  const [backfillXbeeOnly, setBackfillXbeeOnly] = useState(false);
  const [backfillIncludeMaintenance, setBackfillIncludeMaintenance] = useState(false);
  const [backfillUpdateLatestMetadata, setBackfillUpdateLatestMetadata] = useState(false);
  const isLatestPacketsMode = queryMode === 'latest';
  const isDateRangeMode = queryMode === 'dateRange';

  const resolvedRange = useMemo(() => {
    const relativeRange = resolveRelativeRange(timePreset, relativeAnchor);
    if (relativeRange) return relativeRange;
    return { from, to };
  }, [from, relativeAnchor, timePreset, to]);

  const resolvedRangeIso = useMemo(() => ({
    from: toIsoFromLocalDateTime(resolvedRange.from),
    to: toIsoFromLocalDateTime(resolvedRange.to),
  }), [resolvedRange.from, resolvedRange.to]);
  const invalidDateTimeFields = useMemo(
    () => getInvalidDateTimeFields(resolvedRangeIso.from, resolvedRangeIso.to),
    [resolvedRangeIso.from, resolvedRangeIso.to],
  );
  const hasInvalidDateTimeRange = isDateRangeMode && timePreset === 'custom' && invalidDateTimeFields.length > 0;

  const filters = useMemo(() => ({
    ignoreTimeRange: isLatestPacketsMode,
    ...(!isLatestPacketsMode && resolvedRangeIso.from && resolvedRangeIso.to
      ? {
          from: resolvedRangeIso.from,
          to: resolvedRangeIso.to,
        }
      : {}),
    search: debouncedSearch.trim() || undefined,
    source: source !== 'all' ? source : undefined,
    status: status !== 'all' ? status : undefined,
    pageNumber,
    pageLength,
  }), [debouncedSearch, isLatestPacketsMode, pageLength, pageNumber, resolvedRangeIso.from, resolvedRangeIso.to, source, status]);

  const rawEventsQuery = useTransmitterRawEvents(filters);
  const events = rawEventsQuery.data?.events || [];
  const total = rawEventsQuery.data?.total || 0;
  const hasMore = rawEventsQuery.data?.hasMore ?? ((pageNumber + 1) * pageLength < total);
  const totalPages = rawEventsQuery.data?.hasMore === undefined
    ? Math.max(1, Math.ceil(total / pageLength))
    : pageNumber + 1 + (hasMore ? 1 : 0);
  const archiveDescription = (() => {
    if (events.length === 0) return 'No raw events found for the current filters';
    if (rawEventsQuery.data?.hasMore === undefined && total > 0) {
      return `Showing ${events.length} of ${total} raw events`;
    }
    return hasMore
      ? `Showing ${events.length} raw events; more available`
      : `Showing ${events.length} raw events`;
  })();
  const selectedEventKey = useMemo(() => (
    selectedEventSummary
      ? {
          receivedAt: selectedEventSummary.received_at,
          source: selectedEventSummary.source,
          messageId: selectedEventSummary.message_id,
        }
      : null
  ), [selectedEventSummary]);
  const selectedEventDetailQuery = useTransmitterRawEventDetail(selectedEventKey);
  const selectedEvent: TransmitterRawEventDetail | undefined = selectedEventDetailQuery.data;
  const prettyPayloadJson = useMemo(
    () => tryPrettifyJson(selectedEvent?.payload_json),
    [selectedEvent?.payload_json],
  );
  const displayedPayloadJson = showPrettyPayloadJson && prettyPayloadJson
    ? prettyPayloadJson
    : (selectedEvent?.payload_json || '—');
  const backfillCommand = useMemo(() => {
    const limitPerStream = positiveIntegerOrNull(backfillLimitPerStream);
    const maxWritesPerStream = positiveIntegerOrNull(backfillMaxWritesPerStream);
    const maxTotalWrites = backfillScope === 'organisation'
      ? positiveIntegerOrNull(backfillMaxTotalWrites)
      : null;
    const fromIso = resolvedRangeIso.from;
    const toIso = resolvedRangeIso.to;
    const target = backfillScope === 'stream'
      ? backfillStream.trim()
      : backfillOrganisation.trim();

    if (!target || !limitPerStream || !maxWritesPerStream || !fromIso || !toIso) return '';

    const args = [
      'bun',
      'run',
      backfillScope === 'stream' ? 'backfill:raw-events-to-mongo' : 'backfill:raw-events-to-mongo-org',
      '--',
    ];

    if (backfillScope === 'stream') {
      args.push('--stream', shellQuote(target));
      args.push('--limit', shellQuote(limitPerStream));
      args.push('--max-writes', shellQuote(maxWritesPerStream));
    } else {
      const organisationMaxTotalWrites = maxTotalWrites;
      if (!organisationMaxTotalWrites) return '';
      args.push(organisationFlag(target), shellQuote(target));
      args.push('--limit-per-stream', shellQuote(limitPerStream));
      args.push('--max-writes-per-stream', shellQuote(maxWritesPerStream));
      args.push('--max-total-writes', shellQuote(organisationMaxTotalWrites));
      if (backfillXbeeOnly) args.push('--xbee-only');
    }

    args.push(
      '--from',
      shellQuote(fromIso),
      '--to',
      shellQuote(toIso),
      '--source',
      shellQuote(backfillSource),
      '--status',
      shellQuote(backfillStatus),
      '--url',
      shellQuote(backfillUrl),
    );

    if (backfillMode === 'execute') args.push('--execute');
    if (backfillIncludeMaintenance) args.push('--include-maintenance');
    if (backfillUpdateLatestMetadata) args.push('--update-latest-metadata');

    return `cd apps/clickhouse_sync && ${args.join(' ')}`;
  }, [
    backfillIncludeMaintenance,
    backfillLimitPerStream,
    backfillMaxTotalWrites,
    backfillMaxWritesPerStream,
    backfillMode,
    backfillOrganisation,
    backfillScope,
    backfillSource,
    backfillStatus,
    backfillStream,
    backfillUpdateLatestMetadata,
    backfillUrl,
    backfillXbeeOnly,
    resolvedRangeIso.from,
    resolvedRangeIso.to,
  ]);

  useEffect(() => {
    const nextParams = new URLSearchParams();
    nextParams.set('mode', queryMode);
    nextParams.set('pageNumber', String(pageNumber));
    nextParams.set('pageLength', String(pageLength));
    if (search.trim()) nextParams.set('search', search.trim());
    if (source !== 'all') nextParams.set('source', source);
    if (status !== 'all') nextParams.set('status', status);
    if (isDateRangeMode) {
      if (timePreset === 'custom') {
        nextParams.set('from', from);
        nextParams.set('to', to);
      } else {
        nextParams.set('preset', timePreset);
      }
    }
    setSearchParams(nextParams, { replace: true });
  }, [from, isDateRangeMode, pageLength, pageNumber, queryMode, search, setSearchParams, source, status, timePreset, to]);

  useEffect(() => {
    setShowPrettyPayloadJson(false);
  }, [selectedEventSummary]);

  useEffect(() => {
    if (backfillDialogOpen) return;
    setBackfillStream(search.trim());
    setBackfillSource(getBackfillSourceForPageSource(source));
    setBackfillStatus(status !== 'all' ? status : 'received');
    setBackfillUrl(getBackfillUrlForPageSource(source));
  }, [backfillDialogOpen, search, source, status]);

  const visibleColumns = (Object.keys(COLUMN_LABELS) as ColumnKey[]).filter((key) => columnVisibility[key] !== false);

  function updateColumnVisibility(key: ColumnKey): void {
    const next = { ...columnVisibility, [key]: !columnVisibility[key] };
    setColumnVisibility(next);
    localStorage.setItem(VISIBILITY_STORAGE_KEY, JSON.stringify(next));
  }

  function resetPage(): void {
    setPageNumber(0);
  }

  function selectQueryMode(mode: QueryMode): void {
    setQueryMode(mode);
    resetPage();
  }

  async function clearRawEventsCache(): Promise<void> {
    try {
      await queryClient.invalidateQueries({ queryKey: transmitterRawEventsKeys.all });
      toast.success('Raw events cache cleared');
    } catch {
      toast.error('Failed to clear raw events cache');
    }
  }

  function exportCurrentPage(): void {
    const rows = [
      ['received_at', 'source', 'status', 'dev_id', 'dev_eui', 'app_eui', 'identifier', 'version', 'payload_preview'],
      ...events.map((event) => [
        event.received_at,
        event.source,
        event.status,
        event.dev_id || '',
        event.dev_eui || '',
        event.app_eui || '',
        event.identifier === null ? '' : String(event.identifier),
        event.version === null ? '' : String(event.version),
        event.payload_preview || '',
      ]),
    ];

    downloadCsv(`transmitter-raw-events-page-${pageNumber + 1}.csv`, rows);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0 space-y-2">
          <h1 className="text-2xl font-bold tracking-tight">Transmitter Raw Events</h1>
          <p className="text-sm text-muted-foreground">
            Debug ingress data before and around the normal transmitter pipeline. Use this page to investigate malformed packets,
            protocol issues, and unexpected payloads.
          </p>
        </div>

        {canUseRawEventsAdminTools && (
          <div className="flex shrink-0 flex-wrap gap-2 self-start md:self-auto md:justify-end">
            <Button
              className="bg-orange-500 text-white hover:bg-orange-600 focus-visible:ring-orange-500"
              onClick={clearRawEventsCache}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Clear Cache
            </Button>

            <Button
              className="bg-orange-500 text-white hover:bg-orange-600 focus-visible:ring-orange-500"
              onClick={() => setBackfillDialogOpen(true)}
            >
              <Terminal className="mr-2 h-4 w-4" />
              Data Backfill
            </Button>
          </div>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Server-side filters for raw archive queries.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Label id="raw-query-mode-label" className="shrink-0">
              Query Mode
            </Label>
            <div
              role="group"
              aria-labelledby="raw-query-mode-label"
              className="inline-flex w-fit rounded-md border bg-muted/30 p-1"
            >
              <Button
                type="button"
                size="sm"
                variant={isLatestPacketsMode ? 'default' : 'ghost'}
                aria-pressed={isLatestPacketsMode}
                className="rounded-sm"
                onClick={() => selectQueryMode('latest')}
              >
                <ListOrdered className="mr-2 h-4 w-4" />
                Latest packets
              </Button>
              <Button
                type="button"
                size="sm"
                variant={isDateRangeMode ? 'default' : 'ghost'}
                aria-pressed={isDateRangeMode}
                className="rounded-sm"
                onClick={() => selectQueryMode('dateRange')}
              >
                <CalendarDays className="mr-2 h-4 w-4" />
                Date range
              </Button>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-8">
            <div className="space-y-2 xl:col-span-2">
              <Label htmlFor="raw-search">Transmitter / Device</Label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="raw-search"
                  type="text"
                  value={search}
                  placeholder="Enter dev_id or dev_eui"
                  className="pl-9 pr-9"
                  onChange={(e) => {
                    setSearch(e.target.value);
                    resetPage();
                  }}
                />
                {search && (
                  <button
                    type="button"
                    aria-label="Clear search"
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                    onClick={() => {
                      setSearch('');
                      resetPage();
                    }}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Source</Label>
              <Select
                value={source}
                onValueChange={(value) => {
                  setSource(value);
                  resetPage();
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SOURCE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={status}
                onValueChange={(value) => {
                  setStatus(value);
                  resetPage();
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Last X Packets</Label>
              <Select
                value={String(pageLength)}
                disabled={isDateRangeMode}
                onValueChange={(value) => {
                  setPageLength(getLastPacketLimit(value));
                  resetPage();
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LAST_PACKET_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={String(option.value)}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Time Range</Label>
              <Select
                value={timePreset}
                disabled={isLatestPacketsMode}
                onValueChange={(value: TimePreset) => {
                  setIgnoredInvalidDateTimeFields([]);
                  setTimePreset(value);
                  if (value !== 'custom') {
                    setRelativeAnchor(Date.now());
                  }
                  resetPage();
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIME_PRESET_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="raw-from">From</Label>
              <Input
                id="raw-from"
                type="datetime-local"
                value={resolvedRange.from}
                disabled={isLatestPacketsMode || timePreset !== 'custom'}
                aria-invalid={isDateRangeMode && timePreset === 'custom' && !resolvedRangeIso.from ? true : undefined}
                onChange={(e) => {
                  setIgnoredInvalidDateTimeFields([]);
                  setQueryMode('dateRange');
                  setTimePreset('custom');
                  setFrom(e.target.value);
                  resetPage();
                }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="raw-to">To</Label>
              <Input
                id="raw-to"
                type="datetime-local"
                value={resolvedRange.to}
                disabled={isLatestPacketsMode || timePreset !== 'custom'}
                aria-invalid={isDateRangeMode && timePreset === 'custom' && !resolvedRangeIso.to ? true : undefined}
                onChange={(e) => {
                  setIgnoredInvalidDateTimeFields([]);
                  setQueryMode('dateRange');
                  setTimePreset('custom');
                  setTo(e.target.value);
                  resetPage();
                }}
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {isDateRangeMode && timePreset !== 'custom' && (
              <p className="text-sm text-muted-foreground">
                Relative ranges are recalculated from the current time when you refresh.
              </p>
            )}
            {ignoredInvalidDateTimeFields.length > 0 && !hasInvalidDateTimeRange && (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                Invalid date/time filter in the URL was ignored. Showing the default time range.
              </p>
            )}
            {hasInvalidDateTimeRange && (
              <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                Enter valid From and To date/time values, or clear filters to reset the range.
              </p>
            )}
          </div>

          <div className="flex w-full flex-wrap items-center justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setSearch('');
                setSource('all');
                setStatus('all');
                setQueryMode(DEFAULT_QUERY_MODE);
                setTimePreset('custom');
                setIgnoredInvalidDateTimeFields([]);
                setFrom(getDefaultFromValue());
                setTo(getDefaultToValue());
                setRelativeAnchor(Date.now());
                setPageNumber(0);
                setPageLength(DEFAULT_LAST_PACKET_LIMIT);
              }}
            >
              Clear Filters
            </Button>

            <Button
              variant="outline"
              onClick={() => {
                if (isDateRangeMode && timePreset !== 'custom') {
                  setRelativeAnchor(Date.now());
                }
                rawEventsQuery.refetch();
              }}
              disabled={rawEventsQuery.isFetching}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${rawEventsQuery.isFetching ? 'animate-spin' : ''}`} />
              Refresh
            </Button>

            <Button
              variant="outline"
              onClick={exportCurrentPage}
              disabled={events.length === 0}
            >
              <Download className="mr-2 h-4 w-4" />
              Export Page CSV
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  <Settings2 className="mr-2 h-4 w-4" />
                  Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {(Object.keys(COLUMN_LABELS) as ColumnKey[]).map((key) => (
                  <DropdownMenuCheckboxItem
                    key={key}
                    checked={columnVisibility[key] !== false}
                    onCheckedChange={() => updateColumnVisibility(key)}
                  >
                    {COLUMN_LABELS[key]}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Archive</CardTitle>
          <CardDescription>{archiveDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          {rawEventsQuery.error ? (
            <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
              Failed to load raw events.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    {visibleColumns.map((column) => (
                      <TableHead key={column}>{COLUMN_LABELS[column]}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rawEventsQuery.isLoading ? (
                    <TableRow>
                      <TableCell colSpan={visibleColumns.length} className="h-24 text-center text-muted-foreground">
                        Loading raw events...
                      </TableCell>
                    </TableRow>
                  ) : events.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={visibleColumns.length} className="h-24 text-center text-muted-foreground">
                        No raw events found.
                      </TableCell>
                    </TableRow>
                  ) : events.map((event) => (
                    <TableRow
                      key={`${event.source}-${event.message_id}-${event.received_at}`}
                      className="cursor-pointer"
                      onClick={() => setSelectedEventSummary(event)}
                    >
                      {visibleColumns.includes('received_at') && (
                        <TableCell className="whitespace-nowrap">{formatNullableDate(event.received_at)}</TableCell>
                      )}
                      {visibleColumns.includes('source') && (
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <span>{event.source}</span>
                            <span className="text-xs text-muted-foreground">{event.source_topic}</span>
                          </div>
                        </TableCell>
                      )}
                      {visibleColumns.includes('status') && (
                        <TableCell>
                          <Badge variant={getStatusVariant(event.status)}>{event.status}</Badge>
                        </TableCell>
                      )}
                      {visibleColumns.includes('device') && (
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <span className="font-medium">{getDeviceLabel(event)}</span>
                            {event.organisation_id && (
                              <span className="text-xs text-muted-foreground">Org: {event.organisation_id}</span>
                            )}
                          </div>
                        </TableCell>
                      )}
                      {visibleColumns.includes('identifier') && (
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <span>{event.transport}</span>
                            <span className="text-xs text-muted-foreground">
                              ID {event.identifier ?? '—'} / V{event.version ?? '—'}
                            </span>
                          </div>
                        </TableCell>
                      )}
                      {visibleColumns.includes('remote') && (
                        <TableCell className="whitespace-nowrap">
                          {event.remote_address ? `${event.remote_address}:${event.remote_port ?? ''}` : '—'}
                        </TableCell>
                      )}
                      {visibleColumns.includes('payload') && (
                        <TableCell className="font-mono text-xs">{event.payload_preview || '—'}</TableCell>
                      )}
                      {visibleColumns.includes('interpret') && (
                        <TableCell className="whitespace-nowrap">
                          {getPacketInterpreterHref(event.packet_interpreter_payload) ? (
                            <Button asChild variant="outline" size="sm">
                              <a
                                href={getPacketInterpreterHref(event.packet_interpreter_payload) || undefined}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`Open ${getDeviceLabel(event)} payload in packet interpreter`}
                                onClick={(clickEvent) => clickEvent.stopPropagation()}
                              >
                                <ExternalLink className="mr-2 h-4 w-4" />
                                Open
                              </a>
                            </Button>
                          ) : (
                            <Button variant="outline" size="sm" disabled>
                              <ExternalLink className="mr-2 h-4 w-4" />
                              Open
                            </Button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Page {pageNumber + 1} of {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setPageNumber((prev) => Math.max(0, prev - 1))}
                disabled={pageNumber === 0}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                onClick={() => setPageNumber((prev) => prev + 1)}
                disabled={!hasMore}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {canUseRawEventsAdminTools && (
        <Dialog open={backfillDialogOpen} onOpenChange={setBackfillDialogOpen}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>Data Backfill</DialogTitle>
              <DialogDescription>
                Generate a clickhouse_sync backfill command for the selected archive window.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                <div className="space-y-2">
                  <Label>Scope</Label>
                  <Select
                    value={backfillScope}
                    onValueChange={(value: BackfillScope) => setBackfillScope(value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="stream">Stream</SelectItem>
                      <SelectItem value="organisation">Organisation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {backfillScope === 'stream' ? (
                  <div className="space-y-2 xl:col-span-2">
                    <Label htmlFor="backfill-stream">Stream</Label>
                    <Input
                      id="backfill-stream"
                      value={backfillStream}
                      onChange={(event) => setBackfillStream(event.target.value)}
                      placeholder="A840414A01872163_3"
                    />
                  </div>
                ) : (
                  <div className="space-y-2 xl:col-span-2">
                    <Label htmlFor="backfill-organisation">Organisation</Label>
                    <Input
                      id="backfill-organisation"
                      value={backfillOrganisation}
                      onChange={(event) => setBackfillOrganisation(event.target.value)}
                      placeholder="0008 or Mongo ObjectId"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Mode</Label>
                  <Select
                    value={backfillMode}
                    onValueChange={(value: BackfillMode) => setBackfillMode(value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dry-run">Dry Run</SelectItem>
                      <SelectItem value="execute">Execute</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Source</Label>
                  <Select value={backfillSource} onValueChange={setBackfillSource}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BACKFILL_SOURCE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={backfillStatus} onValueChange={setBackfillStatus}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BACKFILL_STATUS_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Payload URL</Label>
                  <Select value={backfillUrl} onValueChange={setBackfillUrl}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BACKFILL_URL_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="backfill-limit-per-stream">Limit per stream</Label>
                  <Input
                    id="backfill-limit-per-stream"
                    type="number"
                    min={1}
                    value={backfillLimitPerStream}
                    onChange={(event) => setBackfillLimitPerStream(event.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="backfill-max-writes-per-stream">Max writes per stream</Label>
                  <Input
                    id="backfill-max-writes-per-stream"
                    type="number"
                    min={1}
                    value={backfillMaxWritesPerStream}
                    onChange={(event) => setBackfillMaxWritesPerStream(event.target.value)}
                  />
                </div>

                {backfillScope === 'organisation' && (
                  <div className="space-y-2">
                    <Label htmlFor="backfill-max-total-writes">Max total writes</Label>
                    <Input
                      id="backfill-max-total-writes"
                      type="number"
                      min={1}
                      value={backfillMaxTotalWrites}
                      onChange={(event) => setBackfillMaxTotalWrites(event.target.value)}
                    />
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-4">
                {backfillScope === 'organisation' && (
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="backfill-xbee-only"
                      checked={backfillXbeeOnly}
                      onCheckedChange={setBackfillXbeeOnly}
                    />
                    <Label htmlFor="backfill-xbee-only">XBee streams only</Label>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="backfill-include-maintenance"
                    checked={backfillIncludeMaintenance}
                    onCheckedChange={setBackfillIncludeMaintenance}
                  />
                  <Label htmlFor="backfill-include-maintenance">Include maintenance</Label>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="backfill-update-latest-metadata"
                    checked={backfillUpdateLatestMetadata}
                    onCheckedChange={setBackfillUpdateLatestMetadata}
                  />
                  <Label htmlFor="backfill-update-latest-metadata">Update latest metadata</Label>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Command</Label>
                <pre
                  aria-label="Backfill command"
                  className="max-h-52 overflow-auto rounded-lg bg-muted p-3 text-xs whitespace-pre-wrap break-all"
                >
                  {backfillCommand || 'Complete required fields.'}
                </pre>
              </div>

              <div className="flex justify-end">
                <Button
                  type="button"
                  onClick={() => copyText(backfillCommand, 'Backfill command')}
                  disabled={!backfillCommand}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Copy Command
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={!!selectedEventSummary} onOpenChange={(open) => !open && setSelectedEventSummary(null)}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>Raw Event Detail</DialogTitle>
            <DialogDescription>
              {selectedEventSummary ? `${selectedEventSummary.source} • ${formatNullableDate(selectedEventSummary.received_at)}` : ''}
            </DialogDescription>
          </DialogHeader>

          {selectedEventDetailQuery.isLoading && (
            <div className="rounded-lg border p-4 text-sm text-muted-foreground">
              Loading raw event detail...
            </div>
          )}

          {selectedEventDetailQuery.error && (
            <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
              Failed to load raw event detail.
            </div>
          )}

          {selectedEvent && (
            <div className="max-h-[75vh] space-y-4 overflow-y-auto pr-2">
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <div>
                  <p className="text-xs text-muted-foreground">Status</p>
                  <Badge variant={getStatusVariant(selectedEvent.status)}>{selectedEvent.status}</Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Device</p>
                  <p className="font-medium">{getDeviceLabel(selectedEvent)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Transport</p>
                  <p>{selectedEvent.transport}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Packet</p>
                  <p>ID {selectedEvent.identifier ?? '—'} / V{selectedEvent.version ?? '—'}</p>
                </div>
                <div className="xl:col-span-2">
                  <p className="text-xs text-muted-foreground">Message ID</p>
                  <p className="font-mono text-xs break-all">{selectedEvent.message_id}</p>
                </div>
                <div className="xl:col-span-2">
                  <p className="text-xs text-muted-foreground">SHA256</p>
                  <p className="font-mono text-xs break-all">{selectedEvent.packet_sha256 || '—'}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {selectedEvent.raw_payload && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyText(selectedEvent.raw_payload || '', 'Raw payload')}
                  >
                    <Copy className="mr-2 h-4 w-4" />
                    Copy Raw Payload
                  </Button>
                )}

                {selectedEvent.payload_json && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyText(
                      showPrettyPayloadJson && prettyPayloadJson ? prettyPayloadJson : selectedEvent.payload_json || '',
                      showPrettyPayloadJson && prettyPayloadJson ? 'Prettified payload JSON' : 'Payload JSON',
                    )}
                  >
                    <Copy className="mr-2 h-4 w-4" />
                    {showPrettyPayloadJson && prettyPayloadJson ? 'Copy Prettified JSON' : 'Copy Payload JSON'}
                  </Button>
                )}

                {getPacketInterpreterHref(selectedEvent.packet_interpreter_payload) && (
                  <Button asChild variant="outline" size="sm">
                    <a
                      href={getPacketInterpreterHref(selectedEvent.packet_interpreter_payload) || undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Open In Packet Interpreter
                    </a>
                  </Button>
                )}
              </div>

              {selectedEvent.reason && (
                <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
                  <p className="font-medium text-destructive">Reason</p>
                  <p className="mt-1 text-destructive">{selectedEvent.reason}</p>
                </div>
              )}

              <div className="space-y-3">
                <div>
                  <p className="mb-2 text-sm font-medium">Raw Payload</p>
                  <pre className="max-h-60 overflow-auto rounded-lg bg-muted p-3 text-xs whitespace-pre-wrap break-all">
                    {selectedEvent.raw_payload || '—'}
                  </pre>
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="text-sm font-medium">Payload JSON</p>
                    {prettyPayloadJson && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => setShowPrettyPayloadJson((prev) => !prev)}
                      >
                        <Braces className="mr-1 h-3.5 w-3.5" />
                        {showPrettyPayloadJson ? 'Show Raw' : 'Prettify JSON'}
                      </Button>
                    )}
                  </div>
                  <pre className="max-h-60 overflow-auto rounded-lg bg-muted p-3 text-xs whitespace-pre-wrap break-all">
                    {displayedPayloadJson}
                  </pre>
                </div>

                <div>
                  <p className="mb-2 text-sm font-medium">Attributes JSON</p>
                  <pre className="max-h-60 overflow-auto rounded-lg bg-muted p-3 text-xs whitespace-pre-wrap break-all">
                    {selectedEvent.attributes_json || '—'}
                  </pre>
                </div>

                <div>
                  <p className="mb-2 text-sm font-medium">Metadata JSON</p>
                  <pre className="max-h-60 overflow-auto rounded-lg bg-muted p-3 text-xs whitespace-pre-wrap break-all">
                    {selectedEvent.metadata_json || '—'}
                  </pre>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
